import { useEffect, useRef, useState } from "react";
import { animate, stagger, splitText, eases } from "animejs";
import { useNavigate } from "react-router-dom";
import twemoji from "twemoji";
import styles from "./HomeContent.module.css";

/* IMAGES */
import bgHero from "../assets/Home.png";
import softPlayImg from "../assets/Home/soft play area.jpeg";
import afterSchoolImg from "../assets/Home/after school.jpeg";
import studioImg from "../assets/Home/studio.jpeg";
import trustImg from "../assets/Home/happy horizon.jpeg";

const offers = [
  {
    slug: "indoor-soft-play",
    image: softPlayImg,
    title: "Indoor Soft Play Area",
    points: [
      "Vegetable & Fruit Picking Station – farm-inspired sensory role play",
      "Slides & Ball Pit – movement, giggles & sensory fun",
      "Sand Pit – tactile play for creativity & fine motor skills",
      "Role-Play Stations – storytelling & imaginative play",
      "Obstacle Course – balance, coordination & confidence",
      "LEGO Wall & Building Zone – problem-solving & creative thinking"
    ],
    footerEmoji: "☀️",
    footer: "A happy, safe escape where kids can be kids"
  },
  {
    slug: "after-school-classes",
    image: afterSchoolImg,
    title: "After-School Activity Classes",
    points: [
      "Art & Craft – creativity, self-expression & fine motor skills",
      "Dance – rhythm, coordination & confidence",
      "Shloka Chanting – focus, memory & cultural awareness",
      "Bricks4Kids – LEGO-based STEM learning & problem-solving",
      "Chess – strategic thinking, patience & decision-making"
    ],
    footerEmoji: "🎨",
    footer: "Joyful learning beyond the classroom"
  },
  {
    slug: "studio-space",
    image: studioImg,
    title: "Flexible Studio Space",
    points: [
      "Fitness & wellness sessions",
      "Counselling, therapy & coaching",
      "Podcast recording & content creation",
      "Stand-up comedy shows & open mics",
      "Workshops, meetings & co-working",
      "Private event bookings"
    ],
    footerEmoji: "🎤",
    footer: "A versatile space that adapts to your purpose"
  },
  {
    slug: "why-parents-love-us",
    image: trustImg,
    title: "Why Parents Love Happy Horizon",
    points: [
      "Safe, clean & child-friendly environment",
      "Development-focused activities",
      "Small group sizes for better attention",
      "Flexible bookings for play, classes & events",
      "Spaces that grow with your child"
    ],
    footerEmoji: "❤️",
    footer: "Where fun, trust & thoughtful design come together"
  }
];

export default function HomeContent() {
  const headingRef = useRef(null);
  const underlineRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;

      const split = splitText(headingRef.current, { by: "chars" });
      animate(split.chars, {
        opacity: [0, 1],
        translateY: [18, 0],
        delay: stagger(24),
        duration: 520,
        easing: "easeOutCubic"
      });

      const path = underlineRef.current;
      const len = path.getTotalLength();
      path.style.strokeDasharray = len;
      path.style.strokeDashoffset = len;

      animate(path, {
        strokeDashoffset: [len, 0],
        duration: 800,
        easing: eases.outExpo
      });

      animate(gridRef.current.children, {
        opacity: [0, 1],
        translateY: [40, 0],
        delay: stagger(140),
        duration: 600,
        easing: "easeOutCubic"
      });

      io.disconnect();
    });

    io.observe(gridRef.current);
    return () => io.disconnect();
  }, []);

  return (
    <main className={styles.page}>
      <section
        className={styles.hero}
        style={{ backgroundImage: `url(${bgHero})` }}
      >
        <div className={styles.overlay} />

        <div className={styles.wrapper}>
          <div className={styles.headingWrap}>
            <h2 ref={headingRef} className={styles.heading}>
              What We Offer
            </h2>

            <svg
              className={styles.underline}
              viewBox="0 0 320 20"
              preserveAspectRatio="none"
            >
              <path
                ref={underlineRef}
                d="M10 10 Q160 22 310 10"
                fill="none"
              />
            </svg>
          </div>

          <div ref={gridRef} className={styles.grid}>
            {offers.map((o) => (
              <OfferCard key={o.slug} {...o} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

/* ================= CARD ================= */

function OfferCard({ image, title, points, footer, footerEmoji, slug }) {
  const [open, setOpen] = useState(false);
  const imgRef = useRef(null);
  const overlayRef = useRef(null);
  const navigate = useNavigate();
  const canHover = window.matchMedia("(hover: hover)").matches;

  useEffect(() => {
    animate(imgRef.current, {
      scale: open ? 1.04 : 1,
      duration: 420,
      easing: "easeOutQuart"
    });

    animate(overlayRef.current, {
      opacity: open ? 1 : 0,
      translateY: open ? 0 : 24,
      overflow: "auto",
      duration: 300,
      easing: "easeOutCubic"
    });

    // ✅ PREMIUM EMOJI PARSE
    if (overlayRef.current) {
      twemoji.parse(overlayRef.current, {
        folder: "svg",
        ext: ".svg"
      });
    }
  }, [open]);

  return (
    <article
      className={styles.card}
      onMouseEnter={canHover ? () => setOpen(true) : undefined}
      onMouseLeave={canHover ? () => setOpen(false) : undefined}
      onClick={!canHover ? () => setOpen((v) => !v) : undefined}
    >
      <img ref={imgRef} src={image} alt={title} className={styles.image} />

      {!open && (
        <div className={styles.title}>
          <h3>{title}</h3>
        </div>
      )}

      <div ref={overlayRef} className={styles.cardOverlay}>
        <h4>{title}</h4>

        <ul>
          {points.map((p, i) => {
            const [l, r] = p.split(" – ");
            return (
              <li key={i}>
                <strong>{l}</strong>
                {r && <> – {r}</>}
              </li>
            );
          })}
        </ul>

        <p className={styles.footer}>
          {footer} <span className={styles.footerEmoji}>{footerEmoji}</span>
        </p>

        <button
          className={styles.cta}
          onClick={() => navigate(`/contact`)}
        >
          Check it out
        </button>
      </div>
    </article>
  );
}
